/**
 * OfflineBanner.jsx
 * Bandeau de statut offline affiché en haut de l'application
 */

import React, { useState } from 'react';
import { useOffline } from '../context/OfflineContext';
import { Wifi, WifiOff, RefreshCw, CloudOff, AlertTriangle, CheckCircle, X, Database, Clock } from 'lucide-react';

// ─── Bandeau principal ────────────────────────────────────────────────────
export default function OfflineBanner() {
  const {
    isOnline,
    syncStatus,
    syncProgress,
    pendingCount,
    subscription,
    lastSync,
    triggerSync,
  } = useOffline();

  const [dismissed, setDismissed] = useState(false);

  // Si online et rien à signaler → pas de bandeau
  if (isOnline && pendingCount === 0 && syncStatus === 'idle' && !subscription?.warningThreshold) {
    return null;
  }

  // Ne pas afficher si l'utilisateur a fermé le bandeau non critique
  if (dismissed && isOnline && pendingCount === 0) return null;

  const isSyncing = syncStatus === 'syncing';
  const isError   = syncStatus === 'error';
  const isSuccess = syncStatus === 'success';

  // ── Couleur selon état ───────────────────────────────────────────────
  const getBannerStyle = () => {
    if (!isOnline)                          return 'bg-slate-800 text-white border-slate-700';
    if (isError)                            return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
    if (isSuccess)                          return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    if (pendingCount > 0)                   return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    if (subscription?.warningThreshold)     return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800';
    return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
  };

  // ── Icône selon état ─────────────────────────────────────────────────
  const getIcon = () => {
    if (!isOnline)      return <WifiOff size={15} className="flex-shrink-0" />;
    if (isSyncing)      return <RefreshCw size={15} className="animate-spin flex-shrink-0" />;
    if (isError)        return <AlertTriangle size={15} className="flex-shrink-0" />;
    if (isSuccess)      return <CheckCircle size={15} className="flex-shrink-0" />;
    if (pendingCount > 0) return <CloudOff size={15} className="flex-shrink-0" />;
    return <AlertTriangle size={15} className="flex-shrink-0" />;
  };

  // ── Message selon état ───────────────────────────────────────────────
  const getMessage = () => {
    if (!isOnline) {
      return (
        <span>
          <strong>Mode hors-ligne</strong>
          {pendingCount > 0 && ` · ${pendingCount} opération${pendingCount > 1 ? 's' : ''} en attente`}
          {lastSync && ` · Dernière sync : ${formatDate(lastSync)}`}
        </span>
      );
    }
    if (isSyncing) {
      return <span>{syncProgress?.message || 'Synchronisation en cours…'}</span>;
    }
    if (isSuccess) {
      return <span>Synchronisation terminée ✓</span>;
    }
    if (isError) {
      return <span>Erreur de synchronisation — <button onClick={() => triggerSync()} className="underline font-semibold">Réessayer</button></span>;
    }
    if (pendingCount > 0) {
      return (
        <span>
          <strong>{pendingCount} opération{pendingCount > 1 ? 's' : ''}</strong> en attente de sync
        </span>
      );
    }
    if (subscription?.warningThreshold) {
      return <span>{subscription.label}</span>;
    }
    return null;
  };

  const message = getMessage();
  if (!message) return null;

  return (
    <div className={`flex items-center justify-between gap-3 px-4 py-2 border-b text-xs font-medium ${getBannerStyle()} print:hidden`}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {getIcon()}
        <span className="truncate">{message}</span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Bouton sync si online et items en attente */}
        {isOnline && pendingCount > 0 && !isSyncing && (
          <button
            onClick={() => triggerSync('full')}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/60 dark:bg-black/20 hover:bg-white/80 dark:hover:bg-black/30 transition text-xs font-semibold"
          >
            <RefreshCw size={12} /> Synchroniser
          </button>
        )}

        {/* Fermer si non critique */}
        {(isOnline && pendingCount === 0) && (
          <button onClick={() => setDismissed(true)} className="opacity-60 hover:opacity-100 transition">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Badge compact pour la nav bar ────────────────────────────────────────
export function OfflineBadge() {
  const { isOnline, pendingCount, syncStatus } = useOffline();

  if (isOnline && pendingCount === 0 && syncStatus === 'idle') {
    return null;
  }

  return (
    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
      !isOnline ? 'bg-slate-400' :
      syncStatus === 'syncing' ? 'bg-blue-500 animate-pulse' :
      pendingCount > 0 ? 'bg-amber-500' :
      syncStatus === 'success' ? 'bg-emerald-500' :
      'bg-red-500'
    }`} />
  );
}

// ─── Panneau détaillé (modal ou drawer) ────────────────────────────────────
export function SyncPanel({ onClose }) {
  const {
    isOnline,
    syncStatus,
    pendingCount,
    subscription,
    lastSync,
    localStats,
    triggerSync,
  } = useOffline();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-indigo-600" />
            <h3 className="font-bold text-slate-900 dark:text-white">Mode hors-ligne</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* État connexion */}
          <div className={`flex items-center gap-3 p-3 rounded-xl ${
            isOnline
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
          }`}>
            {isOnline
              ? <Wifi size={18} className="flex-shrink-0" />
              : <WifiOff size={18} className="flex-shrink-0" />
            }
            <div>
              <div className="font-semibold text-sm">{isOnline ? 'Connecté' : 'Hors-ligne'}</div>
              <div className="text-xs opacity-75">
                {isOnline ? 'Synchronisation possible' : 'Travail en mode local'}
              </div>
            </div>
          </div>

          {/* Abonnement */}
          {subscription && (
            <div className={`flex items-center gap-3 p-3 rounded-xl ${
              subscription.valid
                ? subscription.warningThreshold
                  ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700'
            }`}>
              <Clock size={18} className="flex-shrink-0" />
              <div>
                <div className="font-semibold text-sm">
                  {subscription.valid
                    ? `${subscription.daysLeft} jour${subscription.daysLeft > 1 ? 's' : ''} restant${subscription.daysLeft > 1 ? 's' : ''} hors-ligne`
                    : 'Reconnexion requise'
                  }
                </div>
                <div className="text-xs opacity-75">
                  {subscription.valid
                    ? 'Reconnectez-vous au serveur pour renouveler'
                    : subscription.label
                  }
                </div>
              </div>
            </div>
          )}

          {/* Stats locales */}
          {localStats && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Produits',  value: localStats.products },
                { label: 'Clients',   value: localStats.clients },
                { label: 'Factures',  value: localStats.invoices },
                { label: 'Devis',     value: localStats.quotes },
                { label: 'Tickets',   value: localStats.sales },
                { label: 'En attente', value: localStats.pendingSync, highlight: localStats.pendingSync > 0 },
              ].map(({ label, value, highlight }) => (
                <div key={label} className={`text-center p-2 rounded-xl ${
                  highlight ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-slate-50 dark:bg-slate-700/50'
                }`}>
                  <div className={`font-bold text-lg ${highlight ? 'text-amber-600' : 'text-slate-900 dark:text-white'}`}>
                    {value}
                  </div>
                  <div className="text-xs text-slate-500">{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Dernière sync */}
          {lastSync && (
            <div className="text-xs text-slate-400 text-center">
              Dernière sync : {formatDate(lastSync)}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-3">
          {isOnline ? (
            <button
              onClick={() => { triggerSync('full'); onClose(); }}
              disabled={syncStatus === 'syncing'}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-semibold text-sm transition"
            >
              <RefreshCw size={16} className={syncStatus === 'syncing' ? 'animate-spin' : ''} />
              {syncStatus === 'syncing' ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </button>
          ) : (
            <div className="flex-1 text-center text-sm text-slate-400 py-2.5">
              Connexion internet requise pour synchroniser
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Helper format date ────────────────────────────────────────────────────
function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);

  if (diffMin < 1)   return "à l'instant";
  if (diffMin < 60)  return `il y a ${diffMin} min`;
  if (diffMin < 1440) return `il y a ${Math.floor(diffMin / 60)}h`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}